/**
 * What you can put in a map, and what each thing means to the sim.
 *
 * Every authoring kind resolves to one of the existing agent models — a map
 * cannot invent physics, only arrangements. That is deliberate: the sim stays
 * the validated instrument, and "weird" comes from what you place and where,
 * not from new movement code nobody has tested.
 *
 * `roads` on an actor kind is the realism rule from ADR-022 ("in a small alley
 * the cars won't cross, only people and pets"): the validator refuses a car
 * whose route runs down an alley. Deliberate weirdness stays available through
 * an explicit role — see WEIRD_ROLES.
 */

import type { RoadType } from "../network";

/** Which sim agent model backs an authoring kind. */
export type ActorBacking = "vehicle" | "walker" | "stander" | "cat" | "dog" | "research_ped";

export interface ActorKind {
  backing: ActorBacking;
  /** VehicleAgent's own type, when backed by a vehicle. */
  vehicleType?: "motorbike" | "motorcycle" | "car" | "truck" | "bus" | "vendor_cart";
  /** Default speed (m/s) when the map does not say. */
  speed: number;
  /** Roads this kind may travel on. Empty = anywhere. */
  roads: RoadType[];
  /** Collision circle radius and axial body offsets, when not the default. */
  radius?: number;
  body?: number[];
  accel?: number;
  decel?: number;
  /** One line for the editor palette and the AI format doc. */
  blurb: string;
}

export const ACTOR_KINDS: Record<string, ActorKind> = {
  // --- people --------------------------------------------------------------
  walker: {
    backing: "walker",
    speed: 1.2,
    roads: [],
    blurb: "A person walking a path at a natural 1.2 m/s.",
  },
  stander: {
    backing: "stander",
    speed: 0,
    roads: [],
    blurb: "A person standing still — talking, waiting, being in the way.",
  },
  research_pedestrian: {
    backing: "research_ped",
    speed: 1.2,
    roads: [],
    blurb: "THE controlled encounter: an oncoming pedestrian that drifts left, right or not at all. Research maps only.",
  },
  // --- pets ----------------------------------------------------------------
  cat: {
    backing: "cat",
    speed: 2.6,
    roads: [],
    blurb: "Sits, crouches, then dashes across the road.",
  },
  dog: {
    backing: "dog",
    speed: 1.6,
    roads: [],
    blurb: "Sits by the kerb, trots over to say hello, then settles.",
  },
  chicken: {
    backing: "cat",
    speed: 1.9,
    roads: [],
    blurb: "Like a cat, but a chicken. Crosses the road for reasons of its own.",
  },
  // --- vehicles ------------------------------------------------------------
  motorbike: {
    backing: "vehicle",
    vehicleType: "motorbike",
    speed: 6.0,
    roads: ["alley", "street", "roundabout"],
    blurb: "The default Vietnamese street vehicle. Fits anywhere, including alleys.",
  },
  motorcycle: {
    backing: "vehicle",
    vehicleType: "motorcycle",
    speed: 8.0,
    roads: ["alley", "street", "roundabout"],
    blurb: "Bigger, faster, two body circles.",
  },
  car: {
    backing: "vehicle",
    vehicleType: "car",
    speed: 7.0,
    roads: ["street", "roundabout"],
    blurb: "Streets and roundabouts only — a car does not use a 3.6 m alley.",
  },
  truck: {
    backing: "vehicle",
    vehicleType: "truck",
    speed: 5.0,
    roads: ["street", "roundabout"],
    radius: 1.1,
    body: [-2.2, 0, 2.2],
    accel: 1.2,
    decel: 4.0,
    blurb: "A long, slow car. Streets only, and it blocks a lot of them.",
  },
  bus: {
    backing: "vehicle",
    vehicleType: "bus",
    speed: 8.0,
    roads: ["street", "roundabout"],
    radius: 1.25,
    body: [-3.9, -1.3, 1.3, 3.9],
    accel: 0.9,
    decel: 3.2,
    blurb: "A city bus. Ten metres of it, streets only — long enough to hide whatever is behind it.",
  },
  cart: {
    backing: "vehicle",
    vehicleType: "vendor_cart",
    speed: 1.1,
    roads: ["alley", "street"],
    blurb: "A vendor cart at walking pace. Never on a roundabout.",
  },
};

/**
 * Roles that deliberately break a rule. Naming one is how a map says "yes, I
 * meant that" — the validator then downgrades its realism complaint to a
 * warning instead of refusing to load the map.
 */
export const WEIRD_ROLES = new Set([
  "wrong_way",
  "illegal_turn",
  "blocking",
  "broken_down",
  "reversing",
  "lost",
]);

export interface PropKind {
  halfW: number;
  halfD: number;
  height: number;
  blurb: string;
  /**
   * A hole in the road surface, not a thing standing on it.
   *
   * The difference decides what the robot does when it arrives: a low SOLID
   * object is bumped over, a hole is fallen into. `height` cannot tell them
   * apart — a pallet and a pothole are both about 0.2 — so the map format has
   * to say which is which (ADR-043).
   */
  pit?: boolean;
  /**
   * A low ramp the robot DRIVES OVER, never into (ADR-045).
   *
   * A speed bump is the one solid thing on a road that is never an obstacle:
   * meeting it is the point. `bump` props never collide and never shorten the
   * safety model's free distance, in either assist mode; under real physics
   * crossing one costs speed (the rough_ride jolt), which is the entire
   * mechanism of a speed bump. `height` alone cannot express this — a pallet
   * is just as low and is still something you hit.
   */
  bump?: boolean;
}

/**
 * Solid things. `across: true` in the map swaps halfW/halfD, which is how a
 * car parks along z instead of along x.
 */
export const PROP_KINDS: Record<string, PropKind> = {
  parked_car: { halfW: 2.1, halfD: 0.9, height: 1.4, blurb: "A parked car." },
  parked_motorbike: { halfW: 0.3, halfD: 0.9, height: 0.9, blurb: "A parked motorbike." },
  barrier: { halfW: 1.5, halfD: 0.3, height: 1.0, blurb: "Construction barrier — closes a road off." },
  broken_down_truck: { halfW: 3.0, halfD: 1.1, height: 2.6, blurb: "A truck that is not going anywhere. Half the street, gone." },
  bin: { halfW: 0.4, halfD: 0.4, height: 1.0, blurb: "A wheelie bin." },
  crate_stack: { halfW: 0.6, halfD: 0.6, height: 1.2, blurb: "Stacked crates." },
  spilled_crate: { halfW: 1.1, halfD: 0.8, height: 0.4, blurb: "A crate that lost an argument with a corner." },
  mattress: { halfW: 1.0, halfD: 0.7, height: 0.3, blurb: "A mattress in the road. It happens." },
  sofa: { halfW: 1.0, halfD: 0.45, height: 0.8, blurb: "A sofa, mid-house-move." },
  ladder: { halfW: 0.3, halfD: 1.6, height: 0.4, blurb: "A ladder lying across the road." },
  traffic_cone: { halfW: 0.25, halfD: 0.25, height: 0.6, blurb: "One cone. Rarely alone." },
  hole: { halfW: 0.8, halfD: 0.8, height: 0.2, pit: true, blurb: "Roadworks: an open pit 1.6 m across. Go around." },
  pothole: { halfW: 0.45, halfD: 0.45, height: 0.15, pit: true, blurb: "A pothole, usually full of water. Narrower than roadworks, still far wider than a wheel." },
  water_tank: { halfW: 0.7, halfD: 0.7, height: 1.6, blurb: "A plastic water tank someone left out." },
  pallet: { halfW: 0.6, halfD: 0.5, height: 0.2, blurb: "A pallet." },
  open_car_door: {
    halfW: 0.5,
    halfD: 0.45,
    height: 1.15,
    blurb:
      "A car door swung open into the lane \u2014 the dooring hazard. Place it at the kerbside edge of a parked_car.",
  },
  speed_bump: {
    halfW: 1.8,
    halfD: 0.3,
    height: 0.08,
    bump: true,
    blurb:
      "G\u1eddi gi\u1ea3m t\u1ed1c \u2014 the raised kind: a rubber/concrete hump across the lane. Driven over with a jolt, never around.",
  },
};

/** Scenery kinds the renderer draws specially. Anything else gets a generic
 * block, so a map may invent decor without waiting for renderer work. */
export const DECOR_KINDS = [
  "tree",
  "planter",
  "stall",
  "food_shop",
  "gate",
  "sign",
  "apartment",
  "customer",
  "puddle",
  "laundry",
  "shrine",
  "banner",
  // Legacy: the visual-only bump from before the `bump` prop flag existed
  // (ADR-045). Kept so older maps still load; NEW maps should use the
  // speed_bump PROP (raised, with the crossing jolt) or speed_bump_painted
  // (the flat painted kind below).
  "speed_bump",
  // V\u1ea1ch s\u01a1n gi\u1ea3m t\u1ed1c: the PAINTED kind — transverse rumble strips on
  // the surface. Visual only, and rightly so: paint has no physics.
  "speed_bump_painted",
] as const;

/** Default full carriageway width (m) by road kind. */
export const ROAD_WIDTH: Record<"alley" | "street", number> = {
  alley: 3.6,
  street: 9.0,
};

export function actorKind(type: string): ActorKind | null {
  return ACTOR_KINDS[type] ?? null;
}

export function propKind(kind: string): PropKind {
  return (
    PROP_KINDS[kind] ?? { halfW: 0.5, halfD: 0.5, height: 1.0, blurb: "Unknown kind — generic block." }
  );
}
